<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { X } from 'lucide-vue-next'
import type { PropType } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { Input } from '@/components/ui/input'
import BaseIcon from '../icon/BaseIcon.vue'

const props = defineProps({
	onSearch: {
		type: Function as PropType<(value: string) => void>,
		required: true,
	},
	placeholder: {
		type: String,
		default: 'Search',
	},
	modelValue: {
		type: String,
		default: '',
	},
})

const emit = defineEmits<{
	'update:modelValue': [value: string]
	click: [event: MouseEvent]
}>()

const inputValue = computed({
	get: () => props.modelValue,
	set: value => emit('update:modelValue', value),
})

const isFocused = ref(false)

const showClearButton = computed(
	() => inputValue.value.trim() && isFocused.value,
)

const debouncedSearch = useDebounceFn((value: string) => {
	props.onSearch(value)
}, 300)

const handleSearch = () => {
	const value = inputValue.value.trim()
	if (value) {
		debouncedSearch(value)
	}
}

// sync BFcache input value
const handlePageShow = () => {
	const inputElement = document.querySelector(
		'input[type="search"]',
	) as HTMLInputElement
	if (inputElement) {
		inputValue.value = inputElement.value
	}
}

onMounted(() => {
	window.addEventListener('pageshow', handlePageShow)
})

onUnmounted(() => {
	window.removeEventListener('pageshow', handlePageShow)
})
</script>

<template>
	<div
		class="relative flex w-full items-center"
		@click="(e: MouseEvent) => emit('click', e)"
	>
		<Input
			v-model="inputValue"
			@keyup.enter="handleSearch"
			@focus="() => (isFocused = true)"
			@blur="() => (isFocused = false)"
			:placeholder="placeholder"
			type="search"
			class="typography-b2-reg border-1px h-[44px] rounded-full border-n-100 bg-white py-[10px] pl-[52px] pr-48px text-n-950 outline-none focus:border-2 focus:border-n-950 mdp:pl-24px mdp:pr-[100px]"
		/>

		<!-- Clear Button -->
		<div
			v-if="showClearButton"
			@mousedown.prevent="inputValue = ''"
			class="absolute right-16px top-[14px] cursor-pointer rounded-full mdp:right-[56px] mdp:top-[12px] mdp:hover:bg-n-100"
		>
			<div class="bs-new-icon icon-input-close h-16px w-16px mdp:hidden" />
			<X class="hidden h-24px w-24px p-4px mdp:block mdp:text-n-950" />
		</div>

		<!-- Desktop Search Icon -->
		<div
			@click="handleSearch"
			class="absolute right-12px hidden h-32px w-32px cursor-pointer items-center justify-center rounded-full bg-o-500 hover:bg-o-600 mdp:flex"
		>
			<BaseIcon name="search_v2" class="h-16px w-16px fill-white" />
		</div>

		<!-- Mobile Search Icon -->
		<div
			@click="handleSearch"
			class="absolute left-16px flex h-24px w-24px cursor-pointer items-center justify-center rounded-full mdp:hidden"
		>
			<BaseIcon name="search_v2" class="fill-black" />
		</div>
	</div>
</template>
